import {useOrganization} from 'organization/OrganizationProvider'
import {useEvent} from 'Event/EventProvider'
import {api} from 'lib/url'
import React, {useCallback, useState, useEffect} from 'react'
import {FileLocation} from 'lib/http-client'
import {useAsync} from 'lib/async'

import Page from 'organization/Event/Page'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'

/**
 * Image Entry statuses. Values come from the backend, and these
 * need to be updated if they're modified.
 */
export const ALL = 'all'
export const PENDING = 'pending'
export const APPROVED = 'approved'
export const REJECTED = 'rejected'

export type PageLimit = 10 | 25 | 50 | 100
export const DEFAULT_PAGE_LIMIT: PageLimit = 25

export interface ImageEntry {
  id: string
  title?: string
  description?: string
  file: FileLocation
  status: typeof PENDING | typeof APPROVED | typeof REJECTED
  attendee_id: number
}

export interface AttendeeName {
  first_name: string
  last_name: string
}

interface ImageEntryProviderContextProps {
  entries: ImageEntry[]
  attendeeNames: AttendeeName[]
  approve: (image: ImageEntry) => Promise<void>
  reject: (image: ImageEntry) => Promise<void>
  statusFilter: ImageEntry['status']
  filterByStatus: (status: ImageEntry['status']) => void
  updatePerPage: (limit: PageLimit) => void
  pageLimit: PageLimit
  hasNextPage: boolean
  hasPrevPage: boolean
  goNextPage: () => void
  goPrevPage: () => void
}

const ImageEntriesProviderContext = React.createContext<
  undefined | ImageEntryProviderContextProps
>(undefined)

export default function ImageEntriesProvider(props: {
  children: React.ReactElement
}) {
  const [entries, setEntries] = useState<ImageEntry[]>([])
  const [attendeeNames, setAttendeeNames] = useState<AttendeeName[]>([])
  const [status, setStatus] = useState<ImageEntry['status']>(PENDING)
  const [startId, setStartId] = useState<string>('')
  const [pageLimit, setPageLimit] = useState<PageLimit>(DEFAULT_PAGE_LIMIT)
  const {loading, data: list} = useFetchEntries({
    status,
    startId,
    limit: pageLimit,
  })
  const requestApprove = useApproveEntry()
  const requestReject = useRejectEntry()
  const [hasNextPage, setHasNextPage] = useState(false)

  const [prevPageIds, setPrevPageIds] = useState<string[]>([])

  const remove = (entry: ImageEntry) => {
    setEntries((entries) => {
      return entries.filter((e) => e.id !== entry.id)
    })
  }

  const approve = (entry: ImageEntry) => requestApprove(entry).then(remove)
  const reject = (entry: ImageEntry) => requestReject(entry).then(remove)

  const goToFirstPage = () => {
    setStartId('')
    setPrevPageIds([])
  }

  const filterByStatus = (status: ImageEntry['status']) => {
    setStatus(status)
    goToFirstPage()
  }

  const changeLimit = (pageLimit: PageLimit) => {
    setPageLimit(pageLimit)
    goToFirstPage()
  }

  const hasEntries = entries.length > 0

  const goNextPage = () => {
    if (!hasEntries || !hasNextPage) {
      return
    }

    const lastItem = entries[entries.length - 1]
    setStartId(lastItem.id)

    /**
     * Track previous page ids
     */
    const currentFirst = entries[0]
    setPrevPageIds((existingIds) => [...existingIds, currentFirst.id])
  }

  const hasPrevPage = prevPageIds.length > 0

  const goPrevPage = () => {
    if (!hasPrevPage) {
      return
    }

    const otherIds = [...prevPageIds]
    const prevId = otherIds.pop()

    setPrevPageIds(otherIds)
    /**
     * pop should always have an item, but in case it doesn't, we'll
     * just set an empty start (first page).
     */
    setStartId(prevId || '')
  }

  useEffect(() => {
    if (!list) {
      return
    }

    setEntries(list.items)
    setAttendeeNames(list.attendees)
    setHasNextPage(list.has_next_page)
  }, [list])

  if (loading || !list) {
    return (
      <Page>
        <FullPageLoader />
      </Page>
    )
  }
  return (
    <ImageEntriesProviderContext.Provider
      value={{
        entries,
        attendeeNames,
        approve,
        reject,
        filterByStatus,
        updatePerPage: changeLimit,
        pageLimit,
        hasNextPage,
        goNextPage,
        hasPrevPage,
        goPrevPage,
        statusFilter: status,
      }}
    >
      {props.children}
    </ImageEntriesProviderContext.Provider>
  )
}

function useApproveEntry() {
  const {client} = useOrganization()

  return (imageEntry: ImageEntry) => {
    const url = api(`/image_entries/${imageEntry.id}/approve`)

    return client.put<ImageEntry>(url)
  }
}

function useRejectEntry() {
  const {client} = useOrganization()

  return (imageEntry: ImageEntry) => {
    const url = api(`/image_entries/${imageEntry.id}/reject`)
    return client.put<ImageEntry>(url)
  }
}

type FetchEntriesParams = {
  status: ImageEntry['status']
  startId?: string
  limit?: number
}

export function useFetchEntries(params: FetchEntriesParams) {
  const request = useFetchEntriesRequest(params)
  return useAsync(request)
}

export function useFetchEntriesRequest({
  status,
  startId = '',
  limit = DEFAULT_PAGE_LIMIT,
}: FetchEntriesParams) {
  const {client} = useOrganization()
  const {
    event: {id: eventId},
  } = useEvent()

  return useCallback(() => {
    const url = api(
      `/events/${eventId}/image_entries?status=${status}&start_id=${startId}&limit=${limit}`,
    )

    return client.get<{
      has_next_page: boolean
      items: ImageEntry[]
      attendees: AttendeeName[]
    }>(url)
  }, [status, startId, limit, client, eventId])
}

export function useImageEntries() {
  const context = React.useContext(ImageEntriesProviderContext)

  if (context === undefined) {
    throw new Error(
      'useImageEntries must be used within a ImageEntriesProvider',
    )
  }

  return context
}
